import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaServer,
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaCog,
  FaHeadset,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import HeroSection from '../components/sections/HeroSection';
import ServicesSection from '../components/sections/ServicesSection';
import TrustSection from '../components/sections/TrustSection';
import ClientsSection from '../components/sections/ClientsSection';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, ready } = useTranslation();

  if (!ready) {
    return null;
  }

  const handleContactUs = () => {
    navigate('/contact');
  };

  const stats = [
    {
      number: t('homePage.stats.activeOperators.number'),
      label: t('homePage.stats.activeOperators.label'),
      helpText: t('homePage.stats.activeOperators.helpText'),
      color: 'primary.500',
    },
    {
      number: t('homePage.stats.platformVolume.number'),
      label: t('homePage.stats.platformVolume.label'),
      helpText: t('homePage.stats.platformVolume.helpText'),
      color: 'accent.600',
    },
    {
      number: t('homePage.stats.uptime.number'),
      label: t('homePage.stats.uptime.label'),
      helpText: t('homePage.stats.uptime.helpText'),
      color: 'secondary.600',
    },
  ];

  const features = [
    {
      title: t('homePage.features.scalableInfrastructure.title'),
      description: t('homePage.features.scalableInfrastructure.description'),
      icon: FaServer,
    },
    {
      title: t('homePage.features.enterpriseSecurity.title'),
      description: t('homePage.features.enterpriseSecurity.description'),
      icon: FaShieldAlt,
    },
    {
      title: t('homePage.features.advancedAnalytics.title'),
      description: t('homePage.features.advancedAnalytics.description'),
      icon: FaChartLine,
    },
    {
      title: t('homePage.features.multiPlatformSupport.title'),
      description: t('homePage.features.multiPlatformSupport.description'),
      icon: FaUsers,
    },
    {
      title: t('homePage.features.customDevelopment.title'),
      description: t('homePage.features.customDevelopment.description'),
      icon: FaCog,
    },
    {
      title: t('homePage.features.support24x7.title'),
      description: t('homePage.features.support24x7.description'),
      icon: FaHeadset,
    },
  ];

  return (
    <Box>
      <HeroSection />

      <ServicesSection />

      <TrustSection />

      <ClientsSection />

      {/* Stats Section */}
      <Box bg={useColorModeValue('gray.50', 'gray.800')} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading size='2xl' color='primary.500'>
                {t('homePage.stats.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('homePage.stats.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={3} spacing={{ base: 4, md: 8 }} w='full'>
              {stats.map((stat, index) => (
                <Stat key={index} textAlign='center'>
                  <StatNumber
                    fontSize={{ base: '3xl', md: '4xl' }}
                    color={stat.color}
                  >
                    {stat.number}
                  </StatNumber>
                  <StatLabel
                    fontSize={{ base: 'md', md: 'lg' }}
                    color={useColorModeValue('gray.600', 'gray.400')}
                  >
                    {stat.label}
                  </StatLabel>
                  <StatHelpText
                    fontSize={{ base: 'sm', md: 'sm' }}
                    color='gray.500'
                  >
                    {stat.helpText}
                  </StatHelpText>
                </Stat>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box bg={useColorModeValue('gray.50', 'gray.800')} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading size='2xl' color='primary.500'>
                {t('homePage.features.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('homePage.features.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {features.map((feature, index) => (
                <VStack
                  key={index}
                  spacing={4}
                  p={6}
                  bg={useColorModeValue('white', 'gray.700')}
                  borderRadius='xl'
                  boxShadow='md'
                  textAlign='center'
                >
                  <Icon as={feature.icon} boxSize={10} color='primary.500' />
                  <Heading
                    color={useColorModeValue('gray.900', 'white')}
                    size='md'
                  >
                    {feature.title}
                  </Heading>
                  <Text fontSize='sm' color='gray.500'>
                    {feature.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={{ base: 12, md: 16 }}>
        <Container maxW='4xl'>
          <Box
            bg='primary.500'
            color='white'
            p={{ base: 6, md: 8 }}
            borderRadius='xl'
            textAlign='center'
            w='full'
            maxW='4xl'
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              <Heading size={{ base: 'md', md: 'lg' }}>
                {t('homePage.cta.title')}
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} maxW='2xl'>
                {t('homePage.cta.subtitle')}
              </Text>
              <VStack spacing={4} w={{ base: 'full', md: 'auto' }}>
                <HStack
                  spacing={4}
                  flexWrap={{ base: 'wrap', md: 'nowrap' }}
                  justify='center'
                >
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='solid'
                    bg='white'
                    color='primary.500'
                    onClick={handleContactUs}
                    _hover={{ bg: 'gray.100' }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    {t('homePage.cta.scheduleDemo')}
                  </Button>
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='outline'
                    borderColor='white'
                    color='white'
                    onClick={handleContactUs}
                    _hover={{ bg: 'whiteAlpha.200' }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    {t('homePage.cta.contactSales')}
                  </Button>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
